import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import type { AgentToolFrame } from 'src/data/api/streamAgent';
import { createSessionId } from '../../astra/conversation/conversationState';
import {
  generateReviewQuestions,
  type GenerateReviewQuestionsInput,
} from '../logic/generateReviewQuestions';
import type { GeneratedQuestionsResult } from '../logic/parseGeneratedQuestions';

export interface GenerateQuestionsInput {
  /** Free-text prompt/context (1–50000 chars; required even with a SOP number). */
  readonly prompt: string;
  /** Active business unit (scopes SOP search). */
  readonly businessUnitId: string;
  /** Optional SOP number — omit to let the agent choose one from the prompt. */
  readonly sopNumber?: string | undefined;
}

export interface UseGenerateQuestionsResult {
  /**
   * Runs one generation end to end (stream → accumulate → parse) and resolves
   * with the mapped result. Rejects on transport or parse failure so the caller
   * can keep the user on the prompt screen and surface the message. Calling it
   * again while one is in flight aborts the previous turn first.
   */
  readonly generate: (input: GenerateQuestionsInput) => Promise<GeneratedQuestionsResult>;
  /** True while a generation is in flight. */
  readonly isGenerating: boolean;
  /** The latest agent progress (`tool`) frame, for a friendly step indicator;
   *  null before any frame or after a reset. */
  readonly progress: AgentToolFrame | null;
  /** Raw error message from the last failed generation, or null. UI-agnostic. */
  readonly error: string | null;
  /** Clears the current error — call on retry or dismiss. */
  readonly clearError: () => void;
  /** Aborts an in-flight generation (e.g. the user leaves the screen). No-op
   *  when idle. */
  readonly cancel: () => void;
}

/**
 * "Generate with Astra" data hook for AuditWhiz (WHIZ-002 Phase 3.1).
 *
 * Wraps {@link generateReviewQuestions} (the React-free stream → parse
 * orchestration) in a React Query `useMutation`, mirroring
 * {@link useCreateQuestion}'s surface: `generate` plus
 * `isGenerating`/`error`/`clearError`, with `progress` for the loading UI and
 * `cancel` for aborting. The `await` orchestration lives in the logic module,
 * not here, so the hooks layer stays free of it.
 *
 * A fresh `sessionId` is minted per generation (each is an independent turn).
 * Each `generate` aborts any still-running prior turn via an `AbortController`,
 * so a user who re-generates can't have a stale response resolve over the new
 * one — the aborted turn rejects and its result is discarded by React Query
 * (only the latest mutation's result is retained).
 */
export function useGenerateQuestions(): UseGenerateQuestionsResult {
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<AgentToolFrame | null>(null);
  // Abort handle for the in-flight turn, so a new generate (or cancel) tears
  // down the previous stream rather than leaving it running.
  const abortRef = useRef<AbortController | null>(null);
  // Monotonic turn id. Each generate() bumps it; every state-writing handler
  // (progress, error, success) captures the id at call time and writes only
  // while it's still the latest. Aborting a prior turn doesn't instantly stop
  // its stream — a late onProgress frame or the abort rejection can still fire
  // between abort() and teardown — so gating on the turn id is what actually
  // prevents a superseded turn from writing stale progress/error over the new
  // one (rules: async completions must re-check that the originating intent
  // still stands before writing state).
  const turnRef = useRef(0);

  const mutation = useMutation<
    GeneratedQuestionsResult,
    Error,
    GenerateReviewQuestionsInput
  >({
    mutationFn: (input) => generateReviewQuestions(input),
  });

  const { mutateAsync } = mutation;

  const generate = useCallback(
    (input: GenerateQuestionsInput): Promise<GeneratedQuestionsResult> => {
      // Abort a still-running prior turn before starting a new one.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Claim this turn. Handlers below compare against turnRef.current, so once
      // a newer generate() bumps it, this turn's late callbacks become no-ops.
      turnRef.current += 1;
      const turn = turnRef.current;
      const isCurrent = (): boolean => turnRef.current === turn;

      // Fresh turn: clear a stale error and reset progress up front.
      setError(null);
      setProgress(null);

      return mutateAsync({
        ...input,
        sessionId: createSessionId(),
        // Gate progress writes on the turn still being current — an aborted
        // prior stream can emit a trailing frame before it tears down.
        onProgress: (frame) => {
          if (isCurrent()) setProgress(frame);
        },
        signal: controller.signal,
      }).catch((err: unknown) => {
        // Only surface the failure if this turn still owns the UI. A turn
        // superseded by a newer generate (or cancelled) rejects via abort, and
        // writing its error would clobber the newer turn's clean state.
        if (isCurrent()) {
          setError(err instanceof Error ? err.message : 'Failed to generate questions');
        }
        // Re-throw so the caller's own await still sees the rejection.
        throw err;
      });
    },
    [mutateAsync],
  );

  const clearError = useCallback(() => setError(null), []);

  const cancel = useCallback(() => {
    // Bump the turn so any in-flight turn's late callbacks are ignored, then
    // abort its stream.
    turnRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return {
    generate,
    isGenerating: mutation.isPending,
    progress,
    error,
    clearError,
    cancel,
  };
}
